import LmsTitleDescriptionFields from '../../components/LmsTitleDescriptionFields';
import LmsEditorHeader from '../../components/LmsEditorHeader';
import FormActionButtons from '../../components/FormActionButtons';
import { PageLoader } from '../../components/PageLoader';
import LmsAudiencePicker from '../../components/LmsAudiencePicker';
import { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Video,
  Building,
  Trophy,
  HelpCircle,
  UserCheck,
  AlertTriangle,
} from 'lucide-react';
import { lmsAPI } from '../../services/api';
import { useEditFormPageSetup } from '../../hooks/useEditFormPageSetup';
import {
  applyLmsAssignmentDiff,
  diffLmsAssignments,
  splitLmsAssignments,
  type ExistingLmsAssignment,
} from '../../utils/lmsAssignments';
import {
  defaultSessionSlot,
  isSessionSlotInPast,
  toLocalDateString,
} from '../../utils/lmsSessionSchedule';

type SessionType = 'classroom' | 'video_conference';

interface SessionForm {
  title: string;
  description: string;
  session_type: SessionType;
  scheduled_at: string;
  scheduled_time: string;
  duration_minutes: number;
  location: string;
  /** Classroom venue name, kept separate from the street address. */
  venue_name: string;
  trainer_name: string;
  trainer_emp_code: string;
  max_attendees: number | null;
  points_reward: number;
  has_assessment: boolean;
  assessment_pass_score: number;
  /** Whether this session can be attached to a learning journey stage. */
  available_in_journey: boolean;
  /** Whether it also shows in the learner's own Sessions list. */
  available_standalone: boolean;
  /** 'everyone' = the whole project; 'assigned' = only its assignees. */
  audience: 'everyone' | 'assigned';
}

export default function LmsSessionFormPage() {
  const { id, navigate, isEditing, projectId, loading, setLoading, saving, setSaving } =
    useEditFormPageSetup();
  // The next whole hour, not a blank date beside a hard-coded 10:00. The old
  // default was in the past for most of the working day, and a session created
  // on it was rendered Completed straight away.
  const [initialSlot] = useState(() => defaultSessionSlot());
  const [sessionForm, setSessionForm] = useState<SessionForm>({
    title: '',
    description: '',
    session_type: 'classroom',
    scheduled_at: initialSlot.date,
    scheduled_time: initialSlot.time,
    duration_minutes: 60,
    location: '',
    venue_name: '',
    trainer_name: '',
    trainer_emp_code: '',
    max_attendees: null,
    points_reward: 10,
    has_assessment: false,
    assessment_pass_score: 70,
    available_in_journey: true,
    available_standalone: true,
    audience: 'everyone',
  });

  // Audience picked inline. Kept out of sessionForm because it is not part of
  // the session row — it lands in lms_module_assignments, saved as a diff
  // against whatever the session already had.
  const [assignedRoleIds, setAssignedRoleIds] = useState<string[]>([]);
  const [assignedEmployeeCodes, setAssignedEmployeeCodes] = useState<string[]>([]);
  const [existingAssignments, setExistingAssignments] = useState<ExistingLmsAssignment[]>([]);

  const slot = { date: sessionForm.scheduled_at, time: sessionForm.scheduled_time };
  const slotIsInPast = isSessionSlotInPast(slot);

  // Fetch session if editing
  useEffect(() => {
    if (!isEditing || !id) return;

    let cancelled = false;
    (async () => {
      try {
        const [response, assignmentsResponse] = await Promise.all([
          lmsAPI.getSession(id, projectId),
          // Seeds the inline picker so an edit shows the audience the session
          // already has, and so saving diffs against it instead of piling on.
          lmsAPI.getSessionAssignments(id, projectId).catch(() => null),
        ]);
        if (cancelled) return;
        const s = response.data?.data;
        if (!s) {
          alert('Session not found');
          navigate('/lms/sessions');
          return;
        }

        const existing: ExistingLmsAssignment[] = assignmentsResponse?.data?.data || [];
        setExistingAssignments(existing);
        const seeded = splitLmsAssignments(existing);
        setAssignedRoleIds(seeded.roleIds);
        setAssignedEmployeeCodes(seeded.employeeCodes);
        // Backend stores session_type as 'classroom' | 'virtual'; UI uses
        // 'classroom' | 'video_conference'. Split the date / time and map
        // meeting_link back into the single `location` form field for virtual.
        const scheduled = s.scheduled_at ? new Date(s.scheduled_at) : null;
        const datePart = scheduled
          ? `${scheduled.getFullYear()}-${String(scheduled.getMonth() + 1).padStart(2, '0')}-${String(scheduled.getDate()).padStart(2, '0')}`
          : '';
        const timePart = scheduled
          ? `${String(scheduled.getHours()).padStart(2, '0')}:${String(scheduled.getMinutes()).padStart(2, '0')}`
          : initialSlot.time;
        const isVirtual = s.session_type === 'virtual';
        setSessionForm({
          title: s.title ?? '',
          description: s.description ?? '',
          session_type: isVirtual ? 'video_conference' : 'classroom',
          scheduled_at: datePart,
          scheduled_time: timePart,
          duration_minutes: s.duration_minutes ?? 60,
          location: isVirtual ? (s.meeting_link ?? '') : (s.location ?? ''),
          venue_name: s.venue_name ?? '',
          trainer_name: s.trainer_name ?? '',
          trainer_emp_code: s.trainer_emp_code ?? '',
          max_attendees: s.max_participants ?? null,
          points_reward: s.points_reward ?? 0,
          has_assessment: s.has_assessment === true,
          assessment_pass_score: s.assessment_pass_score ?? 70,
          available_in_journey: s.available_in_journey !== false,
          available_standalone: s.available_standalone !== false,
          audience: s.audience === 'assigned' ? 'assigned' : 'everyone',
        });
      } catch (err) {
        console.error('Error loading session:', err);
        alert('Failed to load session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEditing, projectId, navigate, initialSlot.time]);

  const handleSave = async () => {
    if (!sessionForm.title.trim()) {
      alert('Session title is required');
      return;
    }

    if (!sessionForm.scheduled_at) {
      alert('Session date is required');
      return;
    }

    if (!sessionForm.scheduled_time) {
      alert('Session time is required');
      return;
    }

    // Status is derived from scheduled_at + duration against NOW(), so a
    // session created in the past is born Completed — registration closed,
    // check-in closed, nothing a learner can do with it. Refuse to create one.
    // An edit is still allowed to move a session backwards: that is how an
    // admin corrects the record of something that already happened.
    if (!isEditing && isSessionSlotInPast(slot)) {
      alert(
        'This date and time have already passed.\n\n' +
          'A session scheduled in the past is immediately marked Completed, and no ' +
          'learner can register for it or check in. Pick a future date and time.'
      );
      return;
    }

    // 0 is not a cap, it is a typo. Stored verbatim it read as "unlimited"
    // everywhere downstream, so a session an admin meant to close was open to
    // the whole project. Leave the field empty for no limit.
    if (sessionForm.max_attendees !== null && sessionForm.max_attendees < 1) {
      alert('Max attendees must be at least 1. Leave it empty for no limit.');
      return;
    }

    // A learner cannot attend a virtual session without a link, or find a
    // classroom without a venue. Both were previously saveable as blank.
    if (sessionForm.session_type === 'video_conference') {
      const link = sessionForm.location.trim();
      if (!link) {
        alert('A meeting link is required for a video conference session');
        return;
      }
      if (!/^https?:\/\//i.test(link)) {
        alert('The meeting link must start with http:// or https:// so it opens from the app');
        return;
      }
    } else if (!sessionForm.venue_name.trim() && !sessionForm.location.trim()) {
      alert('Add a venue name or an address so attendees know where to go');
      return;
    }

    try {
      setSaving(true);

      // Send an absolute instant, not a bare wall-clock string.
      //
      // `2026-09-10T10:00:00` with no offset is cast by Postgres using the API
      // server's timezone, not the admin's. Scheduling 10:00 from an IST
      // browser against a UTC server stored 10:00 UTC — and every learner then
      // saw the session at 15:30. Converting through Date here pins the instant
      // the admin actually meant, whatever either machine is set to.
      const scheduledDateTime = new Date(
        `${sessionForm.scheduled_at}T${sessionForm.scheduled_time}:00`,
      ).toISOString();

      const payload = {
        title: sessionForm.title,
        description: sessionForm.description,
        session_type: sessionForm.session_type === 'video_conference' ? 'virtual' : 'classroom',
        scheduled_at: scheduledDateTime,
        duration_minutes: sessionForm.duration_minutes,
        location: sessionForm.session_type === 'classroom' ? sessionForm.location : undefined,
        venue_name: sessionForm.session_type === 'classroom' ? sessionForm.venue_name.trim() : '',
        meeting_link: sessionForm.session_type === 'video_conference' ? sessionForm.location : undefined,
        // Always sent, null included: emptying the field means "unlimited", and
        // an absent key would leave the previous cap in place forever.
        max_participants: sessionForm.max_attendees ?? null,
        points_reward: sessionForm.points_reward,
        trainer_name: sessionForm.trainer_name.trim() || undefined,
        trainer_emp_code: sessionForm.trainer_emp_code.trim() || undefined,
        // These two had a whole card in this form but were never sent — the
        // values silently vanished on save and came back as defaults on edit.
        has_assessment: sessionForm.has_assessment,
        assessment_pass_score: sessionForm.assessment_pass_score,
        available_in_journey: sessionForm.available_in_journey,
        available_standalone: sessionForm.available_standalone,
        audience: sessionForm.audience,
      };

      let sessionId = id;
      if (isEditing && id) {
        await lmsAPI.updateSession(id, payload, projectId);
      } else {
        const created = await lmsAPI.createSession(payload, projectId);
        sessionId = created.data?.data?.id;
      }

      // Save the audience against the session that now exists. A failure here
      // is reported on its own: the session itself saved, and telling the admin
      // "failed to save session" would send them back to create a duplicate.
      if (sessionId) {
        const diff = diffLmsAssignments(
          existingAssignments,
          assignedRoleIds,
          assignedEmployeeCodes
        );
        try {
          await applyLmsAssignmentDiff(diff, {
            createAssignments: (inputs) =>
              lmsAPI.createSessionAssignments(sessionId as string, inputs, projectId),
            deleteAssignment: (assignmentId) =>
              lmsAPI.deleteAssignment(assignmentId, projectId),
          });
        } catch (assignmentErr) {
          console.error('Error saving session assignments:', assignmentErr);
          alert(
            'The session was saved, but its assigned roles and employees could not be updated. ' +
              'Open "Assign Learners" from the session list to set them.'
          );
        }
      }

      navigate('/lms/sessions');
    } catch (err) {
      console.error('Error saving session:', err);
      // Prefer the server's own message. The API rejects a few specific things
      // (a past scheduled_at, for one) with an explanation the admin can act
      // on, and "Failed to save session" throws all of it away.
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save session';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <LmsEditorHeader
        onBack={() => navigate('/lms/sessions')}
        title={isEditing ? 'Edit Session' : 'Create Session'}
        subtitle="Schedule a live training session"
        actions={
          <FormActionButtons
            onCancel={() => navigate('/lms/sessions')}
            onSave={handleSave}
            saving={saving}
            saveLabel="Save Session"
          />
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Session Details */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Session Details</h2>

            <div className="space-y-4">
              <LmsTitleDescriptionFields
                title={sessionForm.title}
                onTitleChange={(value) => setSessionForm({ ...sessionForm, title: value })}
                titlePlaceholder="Enter session title"
                description={sessionForm.description}
                onDescriptionChange={(value) => setSessionForm({ ...sessionForm, description: value })}
                descriptionPlaceholder="Describe what this session covers"
                descriptionRows={4}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Session Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setSessionForm({ ...sessionForm, session_type: 'classroom' })}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
                      sessionForm.session_type === 'classroom'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      sessionForm.session_type === 'classroom'
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Building className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Classroom</p>
                      <p className="text-xs text-gray-500">In-person training</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSessionForm({ ...sessionForm, session_type: 'video_conference' })}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-colors ${
                      sessionForm.session_type === 'video_conference'
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      sessionForm.session_type === 'video_conference'
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Video className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">Video Conference</p>
                      <p className="text-xs text-gray-500">Virtual training</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Calendar className="w-4 h-4 inline-block mr-1" />
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={sessionForm.scheduled_at}
                    // Only on create: an edit must still be able to look back
                    // at a session that already ran.
                    min={isEditing ? undefined : toLocalDateString(new Date())}
                    onChange={(e) => setSessionForm({ ...sessionForm, scheduled_at: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Clock className="w-4 h-4 inline-block mr-1" />
                    Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={sessionForm.scheduled_time}
                    onChange={(e) => setSessionForm({ ...sessionForm, scheduled_time: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <select
                    value={sessionForm.duration_minutes}
                    onChange={(e) => setSessionForm({ ...sessionForm, duration_minutes: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                    <option value={180}>3 hours</option>
                    <option value={240}>4 hours</option>
                  </select>
                </div>
              </div>

              {/* Session status is derived from this slot, so a past one is not
                  a soft warning — it is the difference between a session
                  learners can join and one that lists as Completed on save. */}
              {slotIsInPast && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800">
                    {isEditing
                      ? 'This date and time have already passed, so the session shows as Completed and is closed to registration and check-in.'
                      : 'This date and time have already passed. A session created here would be marked Completed immediately — pick a future slot before saving.'}
                  </p>
                </div>
              )}

              {sessionForm.session_type === 'classroom' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Building className="w-4 h-4 inline-block mr-1" />
                    Venue Name
                  </label>
                  <input
                    type="text"
                    value={sessionForm.venue_name}
                    onChange={(e) => setSessionForm({ ...sessionForm, venue_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Training Room 2, Tower B"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Shown above the address in the app, so attendees know where they are going
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="w-4 h-4 inline-block mr-1" />
                  {sessionForm.session_type === 'classroom' ? 'Address' : 'Meeting Link'}
                  <span className="text-red-500"> *</span>
                </label>
                <input
                  type={sessionForm.session_type === 'classroom' ? 'text' : 'url'}
                  value={sessionForm.location}
                  onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={sessionForm.session_type === 'classroom' ? 'Enter venue address' : 'https://…'}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {sessionForm.session_type === 'classroom'
                    ? 'Attendees can tap this to open it in maps'
                    : 'Must start with http:// or https:// — attendees tap it to join'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users className="w-4 h-4 inline-block mr-1" />
                  Max Attendees (optional)
                </label>
                <input
                  type="number"
                  value={sessionForm.max_attendees ?? ''}
                  onChange={(e) => setSessionForm({ ...sessionForm, max_attendees: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Leave empty for unlimited"
                  min={1}
                />
              </div>

              {/* Trainer. The columns and the API already supported this; the
                  form had no fields, so it was always blank for learners. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <UserCheck className="w-4 h-4 inline-block mr-1" />
                    Trainer Name
                  </label>
                  <input
                    type="text"
                    value={sessionForm.trainer_name}
                    onChange={(e) => setSessionForm({ ...sessionForm, trainer_name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Who is running this session"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trainer Employee Code</label>
                  <input
                    type="text"
                    value={sessionForm.trainer_emp_code}
                    onChange={(e) => setSessionForm({ ...sessionForm, trainer_emp_code: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Optional"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="lg:col-span-1 space-y-6">
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Rewards</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Trophy className="w-4 h-4 inline-block mr-1 text-yellow-500" />
                Points Reward
              </label>
              <input
                type="number"
                value={sessionForm.points_reward}
                onChange={(e) => setSessionForm({ ...sessionForm, points_reward: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                min={0}
              />
              <p className="text-xs text-gray-500 mt-1">Points awarded for attendance</p>
            </div>

            <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
              <LmsAudiencePicker
                projectId={projectId}
                targetLabel="session"
                audience={sessionForm.audience}
                onAudienceChange={(audience) => setSessionForm({ ...sessionForm, audience })}
                roleIds={assignedRoleIds}
                onRoleIdsChange={setAssignedRoleIds}
                employeeCodes={assignedEmployeeCodes}
                onEmployeeCodesChange={setAssignedEmployeeCodes}
                disabled={saving}
              />

              <p className="text-sm font-medium text-gray-700 pt-1">Availability</p>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sessionForm.available_in_journey}
                    onChange={(e) => setSessionForm({ ...sessionForm, available_in_journey: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Available in learning journeys</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Uncheck to keep it out of the journey builder. Stages already using it are unaffected.
                </p>
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sessionForm.available_standalone}
                    onChange={(e) => setSessionForm({ ...sessionForm, available_standalone: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Show in the app's Sessions list</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Uncheck for a session that should only be reachable inside its journey stage.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              <HelpCircle className="w-5 h-5 inline-block mr-1" />
              Trainer Rating
            </h2>

            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sessionForm.has_assessment}
                  onChange={(e) => setSessionForm({ ...sessionForm, has_assessment: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Trainer rates attendees</span>
              </label>

              {sessionForm.has_assessment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pass Score (%)</label>
                  <input
                    type="number"
                    value={sessionForm.assessment_pass_score}
                    onChange={(e) => setSessionForm({ ...sessionForm, assessment_pass_score: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min={0}
                    max={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum rating to pass. Recorded per attendee when you mark the register.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-lg border border-gray-100 bg-white p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                {sessionForm.session_type === 'classroom' ? (
                  <Building className="w-4 h-4 text-blue-500" />
                ) : (
                  <Video className="w-4 h-4 text-purple-500" />
                )}
                <span className="text-gray-600">
                  {sessionForm.session_type === 'classroom' ? 'Classroom Session' : 'Video Conference'}
                </span>
              </div>

              {sessionForm.scheduled_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {new Date(sessionForm.scheduled_at).toLocaleDateString('en-IN', {
                      weekday: 'short',
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">
                  {sessionForm.scheduled_time} · {sessionForm.duration_minutes} min
                </span>
              </div>

              {sessionForm.session_type === 'classroom' && sessionForm.venue_name && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 truncate">{sessionForm.venue_name}</span>
                </div>
              )}

              {sessionForm.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 truncate">{sessionForm.location}</span>
                </div>
              )}

              {sessionForm.trainer_name && (
                <div className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600 truncate">{sessionForm.trainer_name}</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-500" />
                <span className="text-gray-600">{sessionForm.points_reward} points</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
